import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import './InstagramMediaModal.pcss';
import {
  selectInstagramListMedia,
  selectInstagramListMediaPaging,
} from '@mm/state/selectors/instagramSelectors';
import { Line, Spinner } from '@mm/components';
import moment from 'moment';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import { PromoterModel } from '@mm/state/model/PromoterModel';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import { useInstagramCRUDActions } from '@mm/state/actions/InstagramCRUDActions';
import { useConnectedPageCRUDActions } from '@mm/state/actions/ConnectedPageCRUDActions';
import { ArrowStatefulIcon } from '@mm/icons';
import {
  ModalHeader,
  ModalBody,
} from '../../../../../common/modals/modal-helpers';
import { ModalInstanceProps } from '../../../../../common/modals/interfaces';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { colors } from '../../../beta/styles/colors';

type Props = {
  promoter: PromoterModel;
};
type OuterProps = Props & ModalInstanceProps;

export const InstagramMediaModal = (props: OuterProps) => {
  const { promoter, closeModal } = props;
  const instgramListMedia = useSelector(selectInstagramListMedia)?.filter(
    media =>
      media.mediaProductType === 'FEED' || media.mediaProductType === 'REELS',
  );
  const instagramListMediaPaging = useSelector(selectInstagramListMediaPaging);
  const selectedMedia = instgramListMedia?.find(
    media => media.id === promoter.postId,
  );
  const fbPage = useSelector(activeFacebookPageSelector);

  const [isFetching, setIsFetching] = useState(false);
  const { getIGListMedia } = useInstagramCRUDActions();
  const { refreshAccessToken } = useConnectedPageCRUDActions();

  const promoterCRUDActions = usePromoterCRUDActions();
  const chatbot = useSelector(activeChatbotSelector);
  const updatePromoter = id => {
    if (promoter) {
      promoterCRUDActions.update(chatbot.id, {
        ...promoter,
        postId: id,
      });
      closeModal();
    }
  };

  const changeMediaPage = async (
    cursor: string = '',
    cursorValue: string = '',
  ) => {
    setIsFetching(true);
    // @ts-ignore
    await getIGListMedia(fbPage?.id, cursor, cursorValue);
    setIsFetching(false);
  };

  return (
    <div className="instagram-media-modal">
      <ModalHeader className="instagram-media-modal__header">
        Select Post
      </ModalHeader>
      <ModalBody className="instagram-media-modal__body">
        <Spinner
          show={isFetching}
          size={30}
          position="relative"
          backgroundColor="none"
        />
        {!instgramListMedia?.length && !isFetching && (
          <div className="instagram-media-modal__body-empty-state">
            {fbPage?.tokenStatus?.status === 'ok' ? (
              <span>No Posts Yet</span>
            ) : (
              <div>
                Cannot retrieve posts with invalid token
                {fbPage && (
                  <PrimaryButton
                    onClick={() => refreshAccessToken(fbPage.id)}
                    className="instagram-media-modal__body-refresh-connection"
                  >
                    Refresh Connection
                  </PrimaryButton>
                )}
              </div>
            )}
          </div>
        )}
        {!isFetching &&
          instgramListMedia?.map(
            ({
              id,
              mediaUrl,
              mediaType,
              permalink,
              timestamp,
              thumbnailUrl,
              mediaProductType,
            }) => (
              <div
                className={classNames('instagram-media-modal__media_item', {
                  'instagram-media-modal__media_item__active':
                    id === selectedMedia?.id,
                })}
                key={id}
              >
                {mediaType === 'IMAGE' ||
                mediaType === 'CAROUSEL_ALBUM' ||
                (mediaProductType === 'REELS' && !mediaUrl) ||
                (mediaType === 'VIDEO' && !mediaUrl) ? (
                  <img
                    className="instagram-media-modal__media-image"
                    src={mediaUrl ? mediaUrl : thumbnailUrl}
                    onClick={() => updatePromoter(id)}
                  />
                ) : (
                  <video
                    className="instagram-media-modal__media-image"
                    muted
                    controls
                    src={mediaUrl}
                    onClick={() => updatePromoter(id)}
                  />
                )}
                <Line />
                <div className="instagram-media-modal__media-metadata">
                  {moment(timestamp).calendar()}
                  <a
                    className="instagram-media-modal__media-link"
                    href={permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View on Instagram
                  </a>
                </div>
              </div>
            ),
          )}
        {!isFetching &&
          instgramListMedia?.length &&
          (instagramListMediaPaging?.previous ||
            instagramListMediaPaging?.next) && (
            <div className="instagram-media-paging">
              <PrimaryButton
                onClick={() =>
                  changeMediaPage(
                    'before',
                    instagramListMediaPaging?.cursors?.before,
                  )
                }
                color={ButtonColor.secondaryBlue}
                leftIcon={
                  <ArrowStatefulIcon
                    direction="left"
                    color={colors.accentBlue}
                  />
                }
                disabled={!instagramListMediaPaging?.previous}
              >
                Previous
              </PrimaryButton>
              <PrimaryButton
                onClick={() =>
                  changeMediaPage(
                    'after',
                    instagramListMediaPaging?.cursors?.after,
                  )
                }
                color={ButtonColor.secondaryBlue}
                rightIcon={
                  <ArrowStatefulIcon
                    direction="right"
                    color={colors.accentBlue}
                  />
                }
                disabled={!instagramListMediaPaging?.next}
              >
                Next
              </PrimaryButton>
            </div>
          )}
      </ModalBody>
    </div>
  );
};
